import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { IdTitle, IIdTitle } from "ui/Scripts/source/models/common/idTitle";
import { ApplicationPermissionType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";
import { IListFilterItem, ListFilterItem } from "source/models/common/listFilterItem";
import { ISavedListFilter } from "source/models/common/savedListFilter";
import { ITypedOption } from "source/models/common/typedOption";
import { ICompanyPageList } from "source/models/company/companyPageList";
import * as companyService from "source/models/services/companyService";
import { IAppSessionState } from "source/models/store/storeTypes";
import { IUnitNodePermissions } from "source/models/unit/unitNodePermissions";
import { HeaderMenuItem, NavigationMenu } from "source/components/app/NavigationMenu";
import { UserMenu } from "source/components/app/UserMenu";
import { ListFilterSaveDialog } from "source/components/framework/Filters/ListFilterSaveDialog";
import { PageContainer } from "source/components/framework/Page/PageContainer";
import { PageContent } from "source/components/framework/Page/PageContent";
import { PageTopBar } from "source/components/framework/Page/PageTopBar";
import { FilterTabs } from "source/components/framework/PageNavMenuItem/FilterTabs";
import { UnitPermissionDialog } from "source/components/unit/Unit/UnitPermissionDialog";
import { CompanyPageListFilterDrawer } from "./CompanyPageListFilterDrawer";
import { CompanyList } from "source/components/company/CompanyList/CompanyList";

/* eslint-disable no-unused-vars */
const enum CompanyPageListActivityFilterId {
    None = "0",
    Active = "1",
    InTesting = "2",
    InActive = "3",
}
/* eslint-enable no-unused-vars */

// CompanyPageList
export interface ICompanyPageListProps {
    appState: IAppSessionState;
    dataId: number;
    pageList: ICompanyPageList;
    onGetCompaniesPage: () => void;
    onLogout: () => void;
    onSetCulture: (culture: string) => void;
    onSaveUserParameters: (parameters: ITypedOption[]) => void;
    onRefreshSessionState: () => void;
}

interface ICompanyPageListState {
    initialized: boolean;
    companyGroups: IIdTitle[];
    showNavPanel: boolean;
    selectedActivityFilterId: string;
    selectedCompanyGroupIds: string[];
    activityFilters: IIdTitle[];
    sortColumn: string;
    sortOrderIsAsc: boolean;
    // Filters
    companyFilterIsSet: boolean;
    showCompanyFilterDialog: boolean;
    companyFilterDialogFilter: IListFilterItem;
    filters: IListFilterItem[];
    savedFilters: IListFilterItem[];
    activeFilterId: string;
    activeSavedFilterId: string;
    showListFilterSaveDialog: boolean;
    listFilterSaveDialogFilter: IListFilterItem;
    listFilterSaveDialogGeneralFilter: any;
    listFilterSaveDialogReservedNames: string[];
    listFilterSaveDialogOnGetGeneralFilter: () => any;
    // Properties
    dataId: number;
    showUnitNodePermissionsDialog: boolean;
    unitNodePermissions: IUnitNodePermissions;
}

export class CompanyPageList extends React.Component<ICompanyPageListProps, ICompanyPageListState> {
    getActivityFilters = (): IIdTitle[] => {
        const result: IIdTitle[] = [];
        result.push(IdTitle.createIdTitle(CompanyPageListActivityFilterId.None, "'" + Translations.All + "'"));
        result.push(IdTitle.createIdTitle(CompanyPageListActivityFilterId.Active, Translations.InUse));
        result.push(IdTitle.createIdTitle(CompanyPageListActivityFilterId.InTesting, Translations.InTesting));
        result.push(IdTitle.createIdTitle(CompanyPageListActivityFilterId.InActive, Translations.NotInUse));
        return result;
    };
    
    getCompanyGroups = (props: ICompanyPageListProps): IIdTitle[] => {
        const result: IIdTitle[] = [];
        result.push(IdTitle.createIdTitle(Base.emptyGuid, "'" + Translations.All + "'"));
        return result.concat(props.pageList ? props.pageList.companyGroups : []);
    };
    
    getFilters = (): IListFilterItem[] => {
        const result: IListFilterItem[] = [];
        result.push(ListFilterItem.createListFilterItem(Translations.Companies, Translations.Companies,
            {
                activityFilterId: CompanyPageListActivityFilterId.Active,
                companyGroupIds: []
            }, "code", true));
        ListFilterItem.sortListFilterItems(result);
        return result;
    };

    getSavedFilters = (props: ICompanyPageListProps): IListFilterItem[] => {
        const result: IListFilterItem[] = [];
        if (!props || !props.pageList) return result;
        for (const companyFilter of props.pageList.companyFilters) {
            result.push(ListFilterItem.createListFilterItemFromSavedListFilter(companyFilter, true));
        }
        for (const userFilter of props.pageList.userFilters) {
            result.push(ListFilterItem.createListFilterItemFromSavedListFilter(userFilter, false));
        }
        ListFilterItem.sortListFilterItems(result);
        return result;
    };

    getCompanyFilterIsSet = (state: ICompanyPageListState): boolean => {
        return CompanyPageListFilterDrawer.filterIsSet(state.activityFilters, state.companyGroups,
            state.selectedActivityFilterId, state.selectedCompanyGroupIds);
    };

    constructor(props: ICompanyPageListProps) {
        super(props);
        const sessionState = props.appState.state;
        const filters = this.getFilters();
        const filterProps = ListFilterItem.getFilterProps(filters[0], "", true, this.getStateFilterPropsFromGeneralFilter);
        const state = {
            ...filterProps,
            initialized: !!props.pageList,
            companyGroups: this.getCompanyGroups(props),
            showNavPanel: sessionState.getShowPageNavigation(),
            activityFilters: this.getActivityFilters(),
            // Filters
            companyFilterIsSet: false,
            activeFilterId: filters[0].id,
            activeSavedFilterId: "",
            filters,
            savedFilters: this.getSavedFilters(props),
            showCompanyFilterDialog: false,
            companyFilterDialogFilter: null,
            showListFilterSaveDialog: false,
            listFilterSaveDialogFilter: null,
            listFilterSaveDialogGeneralFilter: null,
            listFilterSaveDialogReservedNames: [],
            listFilterSaveDialogOnGetGeneralFilter: null,
            // Properties
            dataId: props.dataId,
            showUnitNodePermissionsDialog: false,
            unitNodePermissions: null
        };
        this.state = {
            ...state,
            companyFilterIsSet: this.getCompanyFilterIsSet(state)
        };
    }

    componentDidMount(): void {
        this.props.onGetCompaniesPage();
    }

    componentDidUpdate(prevProps: ICompanyPageListProps, prevState: ICompanyPageListState): void {
        const props = this.props;
        const state = this.state;
        const sessionState = props.appState.state;
        if (prevProps.dataId === props.dataId &&
            Base.equalStrArrays(prevProps.pageList ? prevProps.pageList.companyGroups.map(i => i.id) : [], props.pageList ? props.pageList.companyGroups.map(i => i.id) : [])) return;
        const savedFilters = this.getSavedFilters(props);
        const activeSavedFilterId = savedFilters.find(i => i.id === state.activeSavedFilterId)
            ? state.activeSavedFilterId
            : "";
        const stateChange = {
            dataId: props.dataId,
            initialized: !!props.pageList,
            showNavPanel: state.initialized ? state.showNavPanel : sessionState.getShowPageNavigation(),
            companyGroups: this.getCompanyGroups(props),
            savedFilters,
            activeSavedFilterId
        };
        const newState = { ...state, ...stateChange };
        this.setState({
            ...stateChange,
            companyFilterIsSet: this.getCompanyFilterIsSet(newState)
        });
    }

    handleToggleShowNavPanel = () => {
        this.setState({ showNavPanel: !this.state.showNavPanel });
    };
    
    handleSetSelectedActivityFilterId = (selectedActivityFilterId: string, checked: boolean) => {
        if (!checked) return;
        this.setState({ selectedActivityFilterId });
    };

    handleToggleCompanyGroup = (companyGroupId: string, checked: boolean) => {
        const state = this.state;
        let selectedCompanyGroupIds = state.selectedCompanyGroupIds.filter(i => i !== Base.emptyGuid);
        if (companyGroupId === Base.emptyGuid) {
            if (checked) {
                this.setState({ selectedCompanyGroupIds: [Base.emptyGuid] });
                return;
            } else if (selectedCompanyGroupIds.length < 1 && state.companyGroups.length > 0) {
                selectedCompanyGroupIds.push(state.companyGroups[0].id);
            }
            this.setState({ selectedCompanyGroupIds });
        } else {
            if (checked) {
                if (selectedCompanyGroupIds.indexOf(companyGroupId) < 0) {
                    selectedCompanyGroupIds.push(companyGroupId);
                }
            } else {
                selectedCompanyGroupIds = selectedCompanyGroupIds.filter(i => i !== companyGroupId);
            }
        }
        if (selectedCompanyGroupIds.length < 1) {
            selectedCompanyGroupIds.push(Base.emptyGuid);
        }
        this.setState({ selectedCompanyGroupIds });
    };

    // #region Saved Filter
    getGeneralFilterFromState = (state: ICompanyPageListState): any => {
        return {
            activityFilterId: state.selectedActivityFilterId ? parseInt(state.selectedActivityFilterId, 10) : 0,
            companyGroupIds: state.selectedCompanyGroupIds ? state.selectedCompanyGroupIds.filter(i => i !== Base.emptyGuid) : []
        };
    };

    getStateFilterPropsFromGeneralFilter = (generalFilter: any): any => {
        return {
            selectedActivityFilterId: generalFilter && !Base.isNullOrUndefined(generalFilter.activityFilterId) ? generalFilter.activityFilterId.toString(10) : CompanyPageListActivityFilterId.Active,
            selectedCompanyGroupIds: generalFilter && generalFilter.companyGroupIds && generalFilter.companyGroupIds.length > 0 ? generalFilter.companyGroupIds : [Base.emptyGuid]
        };
    };
    
    getFilterPropsWithState = (listItemFilter: IListFilterItem) => {
        const state = this.state;
        let result = {
            selectedActivityFilterId: state.selectedActivityFilterId,
            selectedCompanyGroupIds: state.selectedCompanyGroupIds,
            sortColumn: state.sortColumn,
            sortOrderIsAsc: state.sortOrderIsAsc
        };
        if (listItemFilter) {
            result = ListFilterItem.getFilterProps(listItemFilter, result.sortColumn, result.sortOrderIsAsc, this.getStateFilterPropsFromGeneralFilter);
        }
        return result;
    };

    handleChangeActiveFilter = (activeFilterId: string) => {
        const state = this.state;
        const filter = state.filters.find(i => i.id === activeFilterId);
        if (!filter) return;
        const filterProps = this.getFilterPropsWithState(filter);
        const activeSavedFilterId = "";
        const newState = { ...state, ...filterProps, activeFilterId, activeSavedFilterId };
        this.setState({
            ...filterProps,
            activeFilterId,
            activeSavedFilterId: "",
            companyFilterIsSet: this.getCompanyFilterIsSet(newState)
        });
    };

    handleChangeActiveSavedFilter = (activeSavedFilterId: string) => {
        const state = this.state;
        const savedFilter = state.savedFilters.find(i => i.id === activeSavedFilterId);
        if (!savedFilter) return;
        const filterProps = this.getFilterPropsWithState(savedFilter);
        const activeFilterId = "";
        const newState = { ...state, ...filterProps, activeFilterId, activeSavedFilterId };
        this.setState({
            ...filterProps,
            activeFilterId,
            activeSavedFilterId,
            companyFilterIsSet: this.getCompanyFilterIsSet(newState)
        });
    };

    handleRemoveSavedFilter = async (savedFilterId: string) => {
        const props = this.props;
        const state = this.state;
        const sessionState = props.appState.state;
        const savedFilter = state.savedFilters.find(i => i.id === savedFilterId);
        if (!savedFilter) return;
        const result = await ListFilterSaveDialog.removeListFilterFromDb(state.savedFilters, sessionState.companyId, savedFilterId, savedFilter.isShared, ApplicationPermissionType.Company);
        if (!result) return;
        const savedFilters = state.savedFilters.filter(i => i.id !== savedFilterId);
        let activeFilterId = "";
        let activeSavedFilterId = "";
        let newfilter: IListFilterItem = null;
        if (savedFilters.length > 0) {
            newfilter = savedFilters[0];
            activeSavedFilterId = newfilter.id;
        } else if (state.filters.length > 0) {
            newfilter = state.filters[0];
            activeFilterId = newfilter.id;
        }
        const filterProps = this.getFilterPropsWithState(newfilter);
        const newState = { ...state, ...filterProps, activeFilterId, activeSavedFilterId, savedFilters };
        this.setState({
            ...filterProps,
            showCompanyFilterDialog: false,
            activeFilterId,
            activeSavedFilterId,
            savedFilters,
            companyFilterIsSet: this.getCompanyFilterIsSet(newState)
        });
    };

    handleFilterSortData = (sortColumn: string, sortOrderIsAsc: boolean) => {
        this.setState({
            sortColumn,
            sortOrderIsAsc,
        });
    };

    updateFiltersAfterSave = (orgFilterId: string, savedListFilter: ISavedListFilter, isShared: boolean) => {
        if (!savedListFilter) return;
        const state = this.state;
        const savedFilters = state.savedFilters.filter(i => i.id !== orgFilterId || i.isShared !== isShared);
        const filter = ListFilterItem.createListFilterItemFromSavedListFilter(savedListFilter, isShared);
        savedFilters.push(filter);
        ListFilterItem.sortListFilterItems(savedFilters);
        this.setState({
            showListFilterSaveDialog: false,
            savedFilters,
            activeFilterId: "",
            activeSavedFilterId: savedListFilter.id,
            companyFilterDialogFilter: filter
        });
    };

    getActiveFilter = (): IListFilterItem => {
        const state = this.state;
        return state.activeFilterId
            ? state.filters.find(i => i.id === state.activeFilterId)
            : state.savedFilters.find(i => i.id === state.activeSavedFilterId);
    };

    handleSaveListFilter = async (saveAs: boolean, onGetGeneralFilter: () => any) => {
        const props = this.props;
        const state = this.state;
        const sessionState = props.appState.state;
        const filter = this.getActiveFilter();
        if (!filter) return;
        if (!saveAs) {
            const savedListFilter = await ListFilterSaveDialog.saveListFilterToDb(filter, this.getGeneralFilterFromState(state), state.sortColumn, state.sortOrderIsAsc, sessionState.companyId, ApplicationPermissionType.Company);
            this.updateFiltersAfterSave(filter.id, savedListFilter, filter.isShared);
        } else {
            this.setState({
                showListFilterSaveDialog: true,
                listFilterSaveDialogFilter: filter,
                listFilterSaveDialogReservedNames: state.savedFilters.map(i => i.id).concat(state.filters.map(i => i.title)),
                listFilterSaveDialogGeneralFilter: this.getGeneralFilterFromState(state),
                listFilterSaveDialogOnGetGeneralFilter: onGetGeneralFilter
            });
        }
    };

    handleSaveListFilterDialogOk = (savedListFilter: ISavedListFilter, isShared: boolean) => {
        this.updateFiltersAfterSave("", savedListFilter, isShared);
    };

    handleSaveListFilterDialogCancel = () => {
        this.setState({
            showListFilterSaveDialog: false
        });
    };
    // #endregion Saved Filter

    // #region Company Filter
    handleOpenCompanyFilterDialog = () => {
        const filter = this.getActiveFilter();
        if (!filter) return;
        this.setState({
            showCompanyFilterDialog: true,
            companyFilterDialogFilter: filter
        });
    };

    onCompanyFilterDialogOk = (closeDialog: boolean, selectedActivityFilterId: string, selectedCompanyGroupIds: string[]): any => {
        const newState = { ...this.state, selectedActivityFilterId, selectedCompanyGroupIds };
        this.setState({
            showCompanyFilterDialog: !closeDialog,
            selectedActivityFilterId,
            selectedCompanyGroupIds,
            companyFilterIsSet: this.getCompanyFilterIsSet(newState)
        });
        return this.getGeneralFilterFromState(newState);
    };
    
    onCompanyFilterDialogCancel = () => {
        this.setState({ showCompanyFilterDialog: false });
    };
    // #endregion Company Filter

    handleOpenPermissions = async () => {
        const sessionState = this.props.appState.state;
        const result = await AppUtils.callService(() => companyService.getUnitNodePermissions(sessionState.companyId), false);
        if (!result) return;
        this.setState({
            showUnitNodePermissionsDialog: true,
            unitNodePermissions: result
        });
    };

    handlePermissionsDialogOk = () => {
        this.setState({
            dataId: Base.getRandomIntInteger(1000000),
            showUnitNodePermissionsDialog: false
        });
    };

    handlePermissionsDialogCancel = () => {
        this.setState({
            showUnitNodePermissionsDialog: false
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        const sessionState = props.appState.state;
        return (
            <PageContainer>
                <NavigationMenu
                    appState={props.appState}
                    activeMenuItem={HeaderMenuItem.Companies}
                    activeCompanyId={sessionState.companyId}
                />
                <PageContent>
                    <PageTopBar>
                        <UserMenu
                            appState={props.appState}
                            onLogout={props.onLogout}
                            onSetCulture={props.onSetCulture}
                            onRefreshSessionState={props.onRefreshSessionState}
                        />
                    </PageTopBar>
                    <CompanyList
                        savedFilter={state.activeSavedFilterId ? state.savedFilters.find(i => i.id === state.activeSavedFilterId) : null}
                        sessionState={sessionState}
                        dataId={state.dataId.toString(10)}
                        preloaded={true}
                        waitingPreloadedData={true}
                        list={props.pageList?.list}
                        listFilterIsSet={state.companyFilterIsSet}
                        companyGroupIds={state.selectedCompanyGroupIds}
                        activityState={state.selectedActivityFilterId}
                        sortColumn={state.sortColumn}
                        sortOrderIsAsc={state.sortOrderIsAsc}
                        onRefreshSessionState={props.onRefreshSessionState}
                        onSetSortData={this.handleFilterSortData}
                        reportTabs={
                            <FilterTabs
                                items={state.savedFilters}
                                standardFilters={state.filters}
                                selectedId={this.state.activeFilterId}
                                selectedSavedId={this.state.activeSavedFilterId}
                                onSetActiveItem={this.handleChangeActiveSavedFilter}
                                onSetActiveStandardItem={this.handleChangeActiveFilter}
                            />
                        }
                        onOpenFiltersDialog={this.handleOpenCompanyFilterDialog}
                        onToggleSettingsDialog={this.handleOpenPermissions}
                    />
                </PageContent>
                {state.showUnitNodePermissionsDialog &&
                    <UnitPermissionDialog
                        companyId={sessionState.companyId}
                        unitNodePermissions={state.unitNodePermissions}
                        onSave={companyService.saveUnitNodePermissions}
                        onOk={this.handlePermissionsDialogOk}
                        onCancel={this.handlePermissionsDialogCancel}
                    />
                }
                <CompanyPageListFilterDrawer
                    open={state.showCompanyFilterDialog}
                    filter={state.companyFilterDialogFilter}
                    companyGroups={state.companyGroups}
                    activityFilters={state.activityFilters}
                    selectedActivityFilterId={state.selectedActivityFilterId}
                    selectedCompanyGroupIds={state.selectedCompanyGroupIds}
                    canRemoveSharedFilters={sessionState.isCompanyAdmin}
                    onSaveFilter={this.handleSaveListFilter}
                    onRemoveFilter={this.handleRemoveSavedFilter}
                    onOk={this.onCompanyFilterDialogOk}
                    onCancel={this.onCompanyFilterDialogCancel}
                />
                {state.showListFilterSaveDialog &&
                    <ListFilterSaveDialog
                        sessionState={sessionState}
                        companyId={sessionState.companyId}
                        applicationPermissionType={ApplicationPermissionType.Company}
                        listFilter={state.listFilterSaveDialogFilter}
                        generalFilter={state.listFilterSaveDialogGeneralFilter}
                        sortColumn={state.sortColumn}
                        sortOrderIsAsc={state.sortOrderIsAsc}
                        reservedNames={state.listFilterSaveDialogReservedNames}
                        onGetGeneralFilter={state.listFilterSaveDialogOnGetGeneralFilter}
                        onOk={this.handleSaveListFilterDialogOk}
                        onCancel={this.handleSaveListFilterDialogCancel}
                    />
                }
            </PageContainer>
        );
    }
}